import { Injectable } from '@angular/core';
import { LocalesService, UtilsQuestionsService } from 'core/services';
import { TranslateService } from '@ngx-translate/core';
import { DropdownQuestion, QuestionGroup, TextboxQuestion, TextareaQuestion, WysiwygQuestion } from 'core/index';
import { Itinerary } from 'core/models';

@Injectable({
  providedIn: 'root'
})
export class ItineraryTranslationsQuestionsService {
  constructor(
    public translate: TranslateService,
    public locales: LocalesService,
    private utilsQuestionsService: UtilsQuestionsService
  ) {}

  /**
   * Get the group of questions for the itinerary translations form
   * @param itinerary, selected itinerary
   * @param translations, list of translations
   * @param selectedTranslation, selected translation of the itinerary
   * @returns the group of questions with the child questions
   */
  public getQuestions(itinerary: Itinerary, translations: any[], selectedTranslationLang?: string) {
    const baseLang = itinerary.lang;
    const baseTranslation = translations.find((translation) => translation.lang === baseLang);
    const selectedTranslation = selectedTranslationLang
      ? translations.find((translation) => translation.lang === selectedTranslationLang)
      : null;

    const questions = [
      this.getTranslationQuestionGroup(baseTranslation, translations, baseLang),
      this.getTranslationQuestionGroup(selectedTranslation, translations, baseLang)
    ];
    return questions;
  }

  /**
   * Get the group of questions for translation
   * @param translation, translation data
   * @param translations, list of itinerary translations
   * @param baseLang, itinerary base lang
   * @returns the group of questions with the child questions
   */
  private getTranslationQuestionGroup(translation: any, translations: any[], baseLang: string): QuestionGroup {
    const isBaseQuestionGroup: boolean = !!translation && translation.lang === baseLang;

    const translationQuestionGroup: QuestionGroup = new QuestionGroup({
      title: isBaseQuestionGroup ? 'nouns.langOrigin' : 'nouns.langFinal',
      isSection: false,
      key: isBaseQuestionGroup ? 'translationBase' : 'translation',
      class: 'cols-item',
      childQuestions: [
        new DropdownQuestion({
          key: 'lang',
          label: 'nouns.lang',
          placeholder: 'itineraries.selectLang',
          class: 'col-2-offset',
          value: translation ? translation['lang'] : null,
          editable: isBaseQuestionGroup || !translation,
          validators: { required: !isBaseQuestionGroup },
          options: translation
            ? translations.map((translation) =>
                this.locales.getExtendedOptions().find((lang) => lang.key === translation.lang)
              )
            : this.locales
                .getExtendedOptions()
                .filter((lang) => translations.every((trans) => trans.lang !== lang.key))
                .map((lang) => {
                  return { ...lang, disabled: lang.key === baseLang };
                }),
          eventHandlers: {
            onChange: (value) => {
              if (isBaseQuestionGroup) {
                const selectedTranslation = translations.find((translation) => translation.lang === value);
                const { lang, title, description, objectives, contents } = selectedTranslation;
                translationQuestionGroup.control.setValue({ lang, title, description, objectives, contents });
              }
            }
          },
          order: 0
        }),
        new TextboxQuestion({
          key: 'title',
          label: 'itineraries.name',
          placeholder: 'itineraries.titlePlaceholder',
          value: translation ? translation['title'] : null,
          editable: !isBaseQuestionGroup,
          validators: !isBaseQuestionGroup
            ? {
                maxLength: 100,
                required: true
              }
            : {},
          order: 1
        }),
        new TextareaQuestion({
          key: 'description',
          label: 'itineraries.description',
          placeholder: 'itineraries.descriptionPlaceholder',
          value: translation ? translation['description'] : null,
          heightBox: 280,
          editable: !isBaseQuestionGroup,
          validators: !isBaseQuestionGroup
            ? {
                maxLength: 1000,
                required: !isBaseQuestionGroup
              }
            : {},
          order: 2
        }),
        new WysiwygQuestion({
          key: 'objectives',
          label: 'itineraries.objetives',
          placeholder: 'itineraries.objetivesPlaceholder',
          value: translation ? translation.objectives : null,
          editable: !isBaseQuestionGroup,
          heightBox: 280,
          order: 3
        }),
        new WysiwygQuestion({
          key: 'contents',
          label: 'itineraries.contents',
          placeholder: 'itineraries.contentsPlaceholder',
          value: translation ? translation.contents : null,
          editable: !isBaseQuestionGroup,
          heightBox: 280,
          order: 4
        })
      ]
    });
    return translationQuestionGroup;
  }
}
